import React from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import styled, { ThemeProvider } from 'styled-components';
import { getBrand } from '../config/brands';

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, ${props => props.theme.secondaryColor} 0%, #c3cfe2 100%);
`;

const Header = styled.header`
  background: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 1rem 0;
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  align-items: center;

  .logo {
    font-size: 1.5rem;
    font-weight: bold;
    color: ${props => props.theme.primaryColor};
    cursor: pointer;
    text-decoration: none;
    transition: opacity 0.2s;

    &:hover {
      opacity: 0.8;
    }
  }
`;

const ContentSection = styled.section`
  max-width: 900px;
  margin: 0 auto;
  padding: 3rem 2rem;
  background: white;
  margin-top: 2rem;
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: bold;
  color: #1f2937;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1rem;
  color: #6b7280;
  margin-bottom: 2rem;
`;

const Section = styled.div`
  margin-bottom: 2rem;

  h2 {
    font-size: 1.5rem;
    font-weight: 600;
    color: ${props => props.theme.primaryColor};
    margin-bottom: 1rem;
    margin-top: 2rem;
  }

  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 0.75rem;
    margin-top: 1.5rem;
  }

  p {
    color: #4b5563;
    line-height: 1.8;
    margin-bottom: 1rem;
  }

  ul {
    margin-left: 1.5rem;
    margin-bottom: 1rem;

    li {
      color: #4b5563;
      line-height: 1.8;
      margin-bottom: 0.5rem;
    }
  }
`;

const FooterSection = styled.footer`
  background: #1f2937;
  color: white;
  padding: 2rem;
  text-align: center;
`;

interface TermsPageProps {}

const TermsPage: React.FC<TermsPageProps> = () => {
  const [searchParams] = useSearchParams();
  const { brand: brandParam } = useParams<{ brand?: string }>();
  // Use brand from URL parameter if present, otherwise use ref query parameter
  const refParam = brandParam || searchParams.get('ref');
  const brand = getBrand(refParam || undefined);

  const handleLogoClick = () => {
    const landingPath = refParam ? `/${refParam}` : '/';
    window.location.href = `https://finaconnect.com${landingPath}`;
  };

  const theme = {
    primaryColor: brand.primaryColor,
    secondaryColor: brand.secondaryColor
  };

  return (
    <ThemeProvider theme={theme}>
      <PageContainer>
        <Header>
          <HeaderContent>
            <div className="logo" onClick={handleLogoClick}>
              {brand.logo ? (
                <img src={brand.logo} alt={brand.name} style={{ height: '40px', maxWidth: '200px', objectFit: 'contain' }} />
              ) : (
                brand.name
              )}
            </div>
          </HeaderContent>
        </Header>

        <ContentSection>
          <Title>Terms of Service</Title>
          <Subtitle>Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</Subtitle>

          <Section>
            <h2>1. Acceptance of Terms</h2>
            <p>
              By accessing or using {brand.name}'s merchant onboarding services, you agree to be bound by these Terms of Service.
              If you do not agree to these terms, please do not use our services.
            </p>
          </Section>

          <Section>
            <h2>2. Services Description</h2>
            <p>
              {brand.name} provides merchant onboarding and account setup services for financial technology solutions.
              Our services include but are not limited to:
            </p>
            <ul>
              {brand.features.map((feature, index) => (
                <li key={index}>{feature}</li>
              ))}
            </ul>
          </Section>

          <Section>
            <h2>3. Eligibility</h2>
            <p>
              You must be at least 18 years old and capable of forming a binding contract to use our services.
              By using our services, you represent and warrant that you meet these eligibility requirements.
            </p>
          </Section>

          <Section>
            <h2>4. Account Registration</h2>
            <p>
              To access certain features of our services, you may be required to register for an account. You agree to:
            </p>
            <ul>
              <li>Provide accurate, current, and complete information during registration</li>
              <li>Maintain and promptly update your account information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Notify us immediately of any unauthorized use of your account</li>
            </ul>
          </Section>

          <Section>
            <h2>5. Use of Services</h2>
            <p>
              You agree to use our services only for lawful purposes and in accordance with these Terms. You agree not to:
            </p>
            <ul>
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe on the rights of others</li>
              <li>Transmit any harmful or malicious code</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Interfere with or disrupt the services</li>
            </ul>
          </Section>

          <Section>
            <h2>6. Intellectual Property</h2>
            <p>
              All content, features, and functionality of our services are owned by {brand.name} or its licensors and are
              protected by copyright, trademark, and other intellectual property laws.
            </p>
          </Section>

          <Section>
            <h2>7. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, {brand.name} shall not be liable for any indirect, incidental,
              special, consequential, or punitive damages arising out of or relating to your use of our services.
            </p>
          </Section>

          <Section>
            <h2>8. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless {brand.name} and its affiliates from any claims, damages, losses,
              liabilities, and expenses arising out of your use of our services or violation of these Terms.
            </p>
          </Section>

          <Section>
            <h2>9. Modifications to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. We will notify you of any material changes by
              posting the updated Terms on our website. Your continued use of our services after such modifications
              constitutes acceptance of the updated Terms.
            </p>
          </Section>

          <Section>
            <h2>10. Termination</h2>
            <p>
              We may terminate or suspend your access to our services immediately, without prior notice or liability,
              for any reason, including breach of these Terms.
            </p>
          </Section>

          <Section>
            <h2>11. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which {brand.name} operates, without regard to its conflict of law provisions.
            </p>
          </Section>

          <Section>
            <h2>12. Contact Information</h2>
            <p>
              If you have any questions about these Terms, please contact us at{' '}
              <a href={`mailto:${brand.contactEmail}`} style={{ color: brand.primaryColor }}>
                {brand.contactEmail}
              </a>
            </p>
          </Section>
        </ContentSection>

        <FooterSection>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px' }}>
            {brand.footerLogo ? (
              <img src={brand.footerLogo} alt={brand.name} style={{ height: '30px', maxWidth: '150px', objectFit: 'contain', marginRight: '10px' }} />
            ) : brand.logo ? (
              <img src={brand.logo} alt={brand.name} style={{ height: '30px', maxWidth: '150px', objectFit: 'contain', marginRight: '10px' }} />
            ) : null}
          </div>
          <p>&copy; {new Date().getFullYear()} {brand.name}. All rights reserved.</p>
          <p>
            Questions? Contact us at{' '}
            <a href={`mailto:${brand.contactEmail}`} style={{ color: brand.primaryColor }}>
              {brand.contactEmail}
            </a>
          </p>
        </FooterSection>
      </PageContainer>
    </ThemeProvider>
  );
};

export default TermsPage;
